import type { Dispatch, SetStateAction } from 'react';
import { ExclamationCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { expiryOptions } from '@app/pages/b2b/QuestionsLibrary/pages/constants';
import { getLanguageOptions, validateOptionalTranslation } from '@app/utils';
import { usePermissions } from '@data/hooks';
import { AnswerType, QuestionDetailsView, QuestionTranslationView } from '@data/sdk/match';
import { Language, Namespace } from '@data/utils/enums';
import {
  AntdCheckbox,
  AntdCol,
  AntdForm,
  AntdInput,
  AntdRadio,
  AntdRow,
  AntdSegmented,
  AntdSelect,
  AntdSkeleton,
  AntdSpace,
  AntdTooltip,
  AntdTypography,
  Button,
} from '@ui/components';
import { QuestionFormValues } from '../../hooks';
import { TypeTag } from './components';
import { getQuestionTypeOptions } from './constants';
import { getQuestionVisibilityOptions } from './utils';
import './styles.css';

type QuestionFormProps = {
  autoTranslateText: boolean;
  contextLanguage: Language;
  currentLanguage: Language;
  form: FormInstance<QuestionFormValues>;
  handleEditQuestion: (values: QuestionFormValues) => Promise<void>;
  handleResetForm: () => void;
  hasDependencies?: boolean;
  initialValues: QuestionFormValues;
  isEnglishTextDirty: boolean;
  isFormDirty: boolean;
  isQuestionLoading: boolean;
  question?: QuestionDetailsView;
  setContextLanguage: Dispatch<SetStateAction<Language>>;
  setCurrentLanguage: Dispatch<SetStateAction<Language>>;
  translations?: QuestionTranslationView[];
};

const { Text } = AntdTypography;

export const QuestionForm = ({
  autoTranslateText,
  form,
  question,
  isFormDirty,
  translations,
  initialValues,
  isEnglishTextDirty,
  contextLanguage,
  currentLanguage,
  hasDependencies,
  isQuestionLoading,
  handleEditQuestion,
  setCurrentLanguage,
  handleResetForm,
  setContextLanguage,
}: QuestionFormProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { IS_REVIEWER } = usePermissions();

  return (
    <AntdForm
      form={form}
      layout="vertical"
      autoComplete="off"
      requiredMark={false}
      onFinish={handleEditQuestion}
      initialValues={initialValues}
      scrollToFirstError={{ behavior: 'smooth' }}
      className="w-full"
      disabled={IS_REVIEWER || isQuestionLoading}
    >
      <AntdRow gutter={24} className="items-center">
        <AntdCol lg={14}>
          {Object.values(Language).map((lang) => (
            <AntdForm.Item
              key={`text.${lang}`}
              name={['text', `${lang}`]}
              className={cx(lang !== currentLanguage && 'hidden')}
              label={
                <AntdTooltip
                  title={
                    currentLanguage !== Language.EN
                      ? t('question_page.details.form.translation_tooltip')
                      : null
                  }
                  className="flex gap-1"
                  placement="right"
                >
                  <Text>{t('question_page.details.form.question')}</Text>
                  {currentLanguage !== Language.EN && !IS_REVIEWER && (
                    <InfoCircleOutlined className="text-primary-600" />
                  )}
                </AntdTooltip>
              }
              rules={[
                ({ getFieldValue }) => ({
                  validator() {
                    if (!getFieldValue(['text', Language.EN])) {
                      return Promise.reject(
                        new Error(t('patient:validations.default_translation')),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
                { max: 1000, message: t('patient:validations.too_long') },
              ]}
            >
              <AntdInput />
            </AntdForm.Item>
          ))}
        </AntdCol>

        <AntdCol>
          <AntdSegmented
            className="lang-segment mt-1 bg-base-200 w-full"
            options={getLanguageOptions(translations)}
            value={currentLanguage}
            onChange={(v) => setCurrentLanguage(v as Language)}
          />
        </AntdCol>
      </AntdRow>
      {currentLanguage === Language.EN && isEnglishTextDirty && (
        <AntdRow className="mb-4">
          <AntdCol lg={14}>
            <AntdForm.Item name="autoTranslateText" valuePropName="checked" className="mb-1">
              <AntdCheckbox>
                {t('trial_page.auto_translate_checkbox')}
                <AntdTooltip title={t('trial_page.auto_translate_tooltip')} className="ml-1">
                  <InfoCircleOutlined className="text-base-500" />
                </AntdTooltip>
              </AntdCheckbox>
              {!autoTranslateText && (
                <AntdTooltip title={t('trial_page.auto_translate_warning_unchecked')}>
                  <ExclamationCircleOutlined className="text-warning" />
                </AntdTooltip>
              )}
            </AntdForm.Item>
          </AntdCol>
        </AntdRow>
      )}

      <AntdRow gutter={24} className="items-center">
        <AntdCol lg={14}>
          {Object.values(Language).map((lang) => (
            <AntdForm.Item
              key={`context.${lang}`}
              name={['context', `${lang}`]}
              className={cx(lang !== contextLanguage && 'hidden')}
              label={
                <AntdTooltip
                  title={
                    contextLanguage !== Language.EN
                      ? t('question_page.details.form.context_tooltip')
                      : null
                  }
                  className="flex gap-1"
                  placement="right"
                >
                  <Text>{t('question_page.details.form.context')}</Text>
                  {contextLanguage !== Language.EN && !IS_REVIEWER && (
                    <InfoCircleOutlined className="text-primary-600" />
                  )}
                </AntdTooltip>
              }
              rules={[
                ({ getFieldValue }) => ({
                  validator() {
                    return validateOptionalTranslation({
                      t,
                      fieldName: 'context',
                      getFieldValue,
                    });
                  },
                }),
              ]}
              extra={t('question_page.details.form.context_description')}
            >
              <AntdInput />
            </AntdForm.Item>
          ))}
        </AntdCol>

        <AntdCol>
          <AntdSegmented
            className="lang-segment mt-1 bg-base-200 w-full"
            options={getLanguageOptions(translations)}
            value={contextLanguage}
            onChange={(v) => setContextLanguage(v as Language)}
          />
        </AntdCol>
      </AntdRow>

      <AntdRow gutter={24} className="mt-6">
        <AntdCol lg={5}>
          <AntdForm.Item
            name="questionType"
            label={t('question_page.details.form.type')}
            className="mb-4"
          >
            <AntdSelect
              className="w-full questionType"
              disabled={IS_REVIEWER}
              options={getQuestionTypeOptions()}
            />
          </AntdForm.Item>
        </AntdCol>

        {question?.answerType === AnswerType.Number && (
          <AntdCol lg={4}>
            <AntdForm.Item
              key="unit"
              name="unit"
              label={<Text>{t('question_page.details.form.unit')}</Text>}
            >
              <AntdInput maxLength={255} />
            </AntdForm.Item>
          </AntdCol>
        )}

        <AntdCol lg={5}>
          <AntdTooltip
            title={
              hasDependencies ? t('question_page.details.form.block_edit_visibility_msg') : null
            }
          >
            <AntdForm.Item
              name="visibilityType"
              className="m-0"
              label={t('question_page.details.form.visibility')}
              rules={[
                {
                  validator: (_, val) =>
                    val
                      ? Promise.resolve()
                      : Promise.reject(t('patient:validations.please_select')),
                },
              ]}
            >
              <AntdRadio.Group disabled={hasDependencies || IS_REVIEWER}>
                {getQuestionVisibilityOptions().map((el) => (
                  <AntdRadio key={el.value} value={el.value}>
                    {el.label}
                  </AntdRadio>
                ))}
              </AntdRadio.Group>
            </AntdForm.Item>
          </AntdTooltip>
        </AntdCol>

        <AntdCol lg={4}>
          <Text className="block mb-3">{t('question_page.details.form.answer_type')}</Text>
          {isQuestionLoading ? (
            <AntdSkeleton.Input className="h-5 w-24" />
          ) : (
            <TypeTag tagType="answer_types" type={question?.answerType} />
          )}
        </AntdCol>

        <AntdCol lg={4}>
          <Text className="block mb-3">{t('question_page.details.form.generation_type')}</Text>
          {isQuestionLoading ? (
            <AntdSkeleton.Input className="h-5 w-24" />
          ) : (
            <TypeTag tagType="generation_types" type={question?.generationType} />
          )}
        </AntdCol>
      </AntdRow>

      <AntdRow gutter={24}>
        <AntdCol lg={5}>
          <AntdForm.Item name="expiry" label={t('question_page.common.expiry')}>
            <AntdSelect options={expiryOptions()} />
          </AntdForm.Item>
        </AntdCol>
      </AntdRow>

      {!IS_REVIEWER && (
        <div className="fixed bottom-0 left-0 w-full py-2 px-0 sm:px-6 border-t border-base-200 border-solid border-x-0 border-b-0 bg-base-100 z-10 text-end">
          <AntdSpace>
            <Button
              type="primary"
              htmlType="submit"
              disabled={!isFormDirty}
              data-testid="question-save-button"
            >
              {t('patient:common.save_changes')}
            </Button>

            <Button onClick={handleResetForm} disabled={!isFormDirty}>
              {t('patient:common.cancel')}
            </Button>
          </AntdSpace>
        </div>
      )}
    </AntdForm>
  );
};
